import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Checkbox,
  ListItemButton,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import io from "socket.io-client";

const API = process.env.REACT_APP_API_URL
  ? String(process.env.REACT_APP_API_URL).replace(/\/$/, "")
  : "https://servermain.sisrevolution.com";

export default function TeacherMessaging() {
  const admin = useSelector((state) => state.admin) || {};
  const schooldetails_ = useSelector((state) => state.schooldetails) || [];
  const school = schooldetails_[0];
  const userId = admin._id != null ? String(admin._id) : "";
  const schoolID = admin.schoolID != null ? String(admin.schoolID) : "";

  const [conversations, setConversations] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [dmOpen, setDmOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [selected, setSelected] = useState([]);

  const socketRef = React.useRef(null);

  const loadConvos = useCallback(async () => {
    if (!schoolID || !userId) return;
    const res = await axios.get(`${API}/api/messaging/conversations`, {
      params: { schoolID, userId },
    });
    setConversations(res.data?.data || []);
  }, [schoolID, userId]);

  const loadTeachers = useCallback(async () => {
    if (!schoolID) return;
    const res = await axios.post(`${API}/api/teachers/getworkersdata/`, {
      status: "Active",
      schoolID,
    });
    const rows = res.data || [];
    setTeachers(rows.filter((t) => String(t._id) !== userId));
  }, [schoolID, userId]);

  useEffect(() => {
    (async () => {
      try {
        await loadConvos();
        await loadTeachers();
      } finally {
        setLoading(false);
      }
    })();
  }, [loadConvos, loadTeachers]);

  useEffect(() => {
    if (!schoolID || !userId) return;
    const s = io.connect(API);
    socketRef.current = s;
    s.emit("join_room", [schoolID, userId]);
    return () => s.disconnect();
  }, [schoolID, userId]);

  useEffect(() => {
    if (!active?._id || !schoolID || !userId) return;
    const s = socketRef.current;
    if (!s) return;
    s.emit("join_thread", { conversationId: String(active._id), userId });
    const onMsg = (data) => {
      if (String(data.conversationId) !== String(active._id)) return;
      setMessages((prev) => {
        const id = data._id ? String(data._id) : `${data.sentTime}`;
        if (prev.some((p) => String(p._id) === id)) return prev;
        return [...prev, data].sort((a, b) => a.sentTime - b.sentTime);
      });
    };
    s.on("thread_message", onMsg);
    return () => s.off("thread_message", onMsg);
  }, [active, schoolID, userId]);

  const openThread = async (c) => {
    setActive(c);
    const res = await axios.get(
      `${API}/api/messaging/conversations/${c._id}/messages`,
      { params: { limit: 100 } }
    );
    setMessages(res.data?.data || []);
    await axios.post(`${API}/api/messaging/messages/read`, {
      conversationId: String(c._id),
      userId,
    });
  };

  const sendThread = async () => {
    if (!text.trim() || !active) return;
    const senderName =
      admin.lastname || admin.othernames
        ? `${admin.lastname || ""} ${admin.othernames || ""}`.trim()
        : "Staff";
    await axios.post(`${API}/api/messaging/messages`, {
      conversationId: String(active._id),
      schoolID,
      senderId: userId,
      senderName,
      body: text.trim(),
    });
    setText("");
  };

  const startDm = async (peerId) => {
    const res = await axios.post(`${API}/api/messaging/conversations/direct`, {
      schoolID,
      userId,
      peerId,
    });
    setDmOpen(false);
    if (res.data?.data) {
      await loadConvos();
      openThread(res.data.data);
    }
  };

  const createGroup = async () => {
    if (!groupTitle.trim() || selected.length === 0) return;
    const res = await axios.post(`${API}/api/messaging/conversations/group`, {
      schoolID,
      userId,
      title: groupTitle.trim(),
      memberIds: selected,
    });
    setGroupOpen(false);
    setGroupTitle("");
    setSelected([]);
    if (res.data?.data) {
      await loadConvos();
      openThread(res.data.data);
    }
  };

  const displayName = (t) =>
    `${t.lastname || ""} ${t.othernames || ""}`.trim() || t.email || "Teacher";

  if (!school) {
    return (
      <Box p={3}>
        <Typography>Load school data first.</Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Teacher messaging
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Direct and group threads (separate from school-wide live chat).
      </Typography>

      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Button variant="contained" onClick={() => setDmOpen(true)}>
          New DM
        </Button>
        <Button variant="contained" color="success" onClick={() => setGroupOpen(true)}>
          New group
        </Button>
        <Button component={Link} to="/livechat" variant="outlined">
          School chat
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined" sx={{ display: "flex", minHeight: 420 }}>
          <List sx={{ width: 320, borderRight: 1, borderColor: "divider" }}>
            {conversations.map((c) => (
              <ListItemButton
                key={c._id}
                selected={active?._id === c._id}
                onClick={() => openThread(c)}
              >
                <ListItemText
                  primary={c.type === "group" ? c.title || "Group" : "Direct"}
                  secondary={c.type}
                />
              </ListItemButton>
            ))}
          </List>
          <Box flex={1} p={2} display="flex" flexDirection="column">
            {!active ? (
              <Typography color="textSecondary">Select a conversation</Typography>
            ) : (
              <>
                <Box flex={1} overflow="auto" mb={1}>
                  {messages.map((m) => {
                    const own = String(m.senderId) === userId;
                    const rb = Array.isArray(m.readBy) ? m.readBy : [];
                    const read =
                      own &&
                      rb.filter((x) => String(x.userId) !== String(m.senderId)).length > 0;
                    return (
                      <Box
                        key={String(m._id)}
                        textAlign={own ? "right" : "left"}
                        mb={1}
                      >
                        <Paper
                          sx={{
                            display: "inline-block",
                            p: 1,
                            bgcolor: own ? "primary.light" : "grey.200",
                          }}
                        >
                          {!own && m.senderName && (
                            <Typography variant="caption">{m.senderName}</Typography>
                          )}
                          <Typography>{m.body}</Typography>
                          <Typography variant="caption" display="block">
                            {new Date(m.sentTime).toLocaleString()}
                            {read ? " · Read" : ""}
                          </Typography>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message"
                  />
                  <Button variant="contained" onClick={sendThread}>
                    Send
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      )}

      <Dialog open={dmOpen} onClose={() => setDmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Direct message</DialogTitle>
        <DialogContent>
          <List>
            {teachers.map((t) => (
              <ListItemButton key={t._id} onClick={() => startDm(String(t._id))}>
                <ListItemText primary={displayName(t)} secondary={t.ID} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDmOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupOpen} onClose={() => setGroupOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Group name"
            value={groupTitle}
            onChange={(e) => setGroupTitle(e.target.value)}
            margin="normal"
          />
          {teachers.map((t) => (
            <ListItem key={t._id} dense>
              <Checkbox
                checked={selected.includes(String(t._id))}
                onChange={() =>
                  setSelected((prev) =>
                    prev.includes(String(t._id))
                      ? prev.filter((x) => x !== String(t._id))
                      : [...prev, String(t._id)]
                  )
                }
              />
              <ListItemText primary={displayName(t)} />
            </ListItem>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createGroup}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
